import { useState } from 'react';
import { postRequest, showMessage } from '../../../config';

const MODULES = [
    { id: 'dataRepo', label: 'Data Repository', endpoint: '/modules/temp-modules/migrate-repo-answers-to-temp', body: (email) => ({ email }) },
    { id: 'businessAudit', label: 'Business Audit', endpoint: '/modules/temp-modules/migrate-ba-answers-to-temp', body: (email) => ({ email }) },
    { id: 'warrantyDisclosure', label: 'Warranty Disclosure', endpoint: '/modules/temp-modules/migrate-wd-answers-to-temp', body: (email) => ({ email }) },
    { id: 'directorsQuestionnaire', label: 'Directors Questionnaire', endpoint: '/modules/temp-modules/migrate-dq-answers-to-temp', body: (email) => ({ email }) },
    { id: 'top10', label: 'Top 10 Investment Readiness Cock Ups To Avoid', endpoint: '/modules/temp-modules/migrate-top10-answers-to-temp', body: (email) => ({ email }) },
    { id: 'healthBoost', label: 'All HealthBoost Modules', endpoint: '/modules/temp-modules/migrate-healthboost-modules', body: (email) => ({ email, modules: ['HEYBP', 'HLTGP'] }) },
];

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SuccessIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ minWidth: 18 }}>
        <circle cx="9" cy="9" r="9" fill="#40C353" />
        <path d="M4.5 9L7.5 12L13.5 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ErrorIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ minWidth: 18 }}>
        <circle cx="9" cy="9" r="9" fill="#F0606C" />
        <path d="M6 6L12 12M12 6L6 12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PendingIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ minWidth: 18 }}>
        <circle cx="9" cy="9" r="9" fill="#DDE2F2" />
        <circle cx="9" cy="9" r="3" fill="#AAACB5" />
    </svg>
);

const StatTile = ({ label, value, color }) => (
    <div className="df fdc aic jcc br10px" style={{ background: '#F7F8FE', border: '1px solid #DDE2F2', padding: '10px 14px', minWidth: 70, flex: 1 }}>
        <span className="fs1-25rem fw600" style={{ color }}>{value ?? '-'}</span>
        <span className="fs11px fw400 c28 tac wsn">{label}</span>
    </div>
);

export default function ModuleWiseMigration() {
    const [email, setEmail] = useState('');
    const [selected, setSelected] = useState({});
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [runningId, setRunningId] = useState(null);

    const toggleModule = (id) => { if (!loading) setSelected(prev => ({ ...prev, [id]: !prev[id] })); };
    const toggleAll = () => {
        const allSelected = MODULES.every(m => selected[m['id']]);
        const next = {};
        MODULES.forEach(m => { next[m['id']] = !allSelected; });
        setSelected(next);
    };

    const selectedModules = MODULES.filter(m => selected[m['id']]);
    const allSelected = MODULES.every(m => selected[m['id']]);

    const handleSubmit = async () => {
        if (!email.trim()) return showMessage('Please enter a user email.', 'error');
        if (!selectedModules.length) return showMessage('Please select at least one module.', 'error');
        setLoading(true);
        setResults(selectedModules.map(m => ({ id: m['id'], label: m['label'], status: 'pending' })));
        for (const mod of selectedModules) {
            setRunningId(mod['id']);
            try {
                const res = await postRequest(mod['endpoint'], mod['body'](email.trim()));
                const success = res['code'] === 200;
                setResults(prev => prev.map(r => r['id'] === mod['id'] ? { ...r, status: success ? 'success' : 'error', message: res['message'] || 'Completed', data: res['body'] } : r));
            } catch (_) {
                setResults(prev => prev.map(r => r['id'] === mod['id'] ? { ...r, status: 'error', message: 'Request failed' } : r));
            }
        }
        setRunningId(null);
        setLoading(false);
    };

    const canSubmit = !loading && selectedModules.length > 0 && email.trim().length > 0;

    return (
        <div className="p2rem w100 df" style={{ gap: 32, alignItems: 'flex-start' }}>

            {/* ── LEFT: Form ── */}
            <div style={{ flex: '0 0 420px', maxWidth: 420 }}>
                <h2 className="c6 fs1-5rem fw600 mb0-5rem">Module Wise Migration</h2>
                <p className="c28 fs14px fw400 mb2rem">Enter the user email and select which modules to migrate answers for.</p>

                {/* Email */}
                <div className="mb1-5rem">
                    <label className="db c15 fs14px fw500 mb0-5rem">User Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e['target']['value'])}
                        placeholder="e.g. user@example.com"
                        disabled={loading}
                        style={{
                            width: '100%', boxSizing: 'border-box', padding: '10px 14px',
                            borderRadius: 10, border: '1px solid #DDE2F2', background: '#F7F8FE',
                            fontSize: 14, color: '#151313', outline: 'none',
                            opacity: loading ? 0.6 : 1,
                        }}
                    />
                </div>

                {/* Module header row */}
                <div className="df aic jcsb mb0-75rem">
                    <span className="c15 fs14px fw500">Select Modules</span>
                    <button
                        onClick={toggleAll}
                        disabled={loading}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: loading ? 'not-allowed' : 'pointer', color: '#4A67FF', fontSize: 13, fontWeight: 500 }}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>

                {/* Module checkbox cards */}
                <div className="df fdc mb2rem" style={{ gap: 8 }}>
                    {MODULES.map((mod) => {
                        const isChecked = !!selected[mod['id']];
                        return (
                            <div
                                key={mod['id']}
                                onClick={() => toggleModule(mod['id'])}
                                className="df aic"
                                style={{
                                    padding: '12px 14px', borderRadius: 10,
                                    border: isChecked ? '2px solid #4A67FF' : '1px solid #DDE2F2',
                                    background: isChecked ? '#F0F2FD' : '#F7F8FE',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 200ms',
                                    userSelect: 'none',
                                }}
                            >
                                {/* Custom checkbox */}
                                <div style={{
                                    width: 20, height: 20, minWidth: 20, borderRadius: 5,
                                    border: isChecked ? '2px solid #4A67FF' : '2px solid #D9D9D9',
                                    background: isChecked ? '#4A67FF' : '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginRight: 12, transition: 'all 200ms',
                                }}>
                                    {isChecked && <CheckIcon />}
                                </div>
                                <span style={{ fontSize: 14, fontWeight: 500, color: '#151313', lineHeight: 1.4 }}>{mod['label']}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Submit button */}
                <button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    style={{
                        padding: '12px 32px', borderRadius: 50, border: 'none',
                        background: canSubmit ? '#4A67FF' : '#AAACB5',
                        color: '#fff', fontSize: 14, fontWeight: 500,
                        cursor: canSubmit ? 'pointer' : 'not-allowed',
                        minWidth: 200, transition: 'background 200ms',
                    }}
                >
                    {loading
                        ? 'Running Migrations...'
                        : `Run Migration${selectedModules.length !== 1 ? 's' : ''}${selectedModules.length > 0 ? ` (${selectedModules.length})` : ''}`
                    }
                </button>
            </div>

            {/* ── RIGHT: Results panel ── */}
            {results.length > 0 && (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="c15 fs1rem fw600 mb1rem">Migration Results</h3>
                    <div className="df fdc" style={{ gap: 12 }}>
                        {results.map((r, i) => {
                            const isRunning = runningId === r['id'];
                            const borderColor = r['status'] === 'success' ? '#40C353' : r['status'] === 'error' ? '#F0606C' : isRunning ? '#4A67FF' : '#DDE2F2';
                            const bgColor = r['status'] === 'success' ? '#F0FFF6' : r['status'] === 'error' ? '#FFF5F5' : isRunning ? '#F0F2FD' : '#F7F8FE';
                            return (
                                <div key={i} style={{ borderRadius: 12, border: `1px solid ${borderColor}`, background: bgColor, padding: '14px 16px', transition: 'all 200ms' }}>
                                    {/* Header row */}
                                    <div className="df aic" style={{ gap: 10, marginBottom: r['data'] ? 10 : 0 }}>
                                        {r['status'] === 'success' && <SuccessIcon />}
                                        {r['status'] === 'error' && <ErrorIcon />}
                                        {r['status'] === 'pending' && !isRunning && <PendingIcon />}
                                        {isRunning && r['status'] === 'pending' && (
                                            <svg width="18" height="18" viewBox="0 0 18 18" style={{ minWidth: 18, animation: 'spin 1s linear infinite' }}>
                                                <circle cx="9" cy="9" r="7" stroke="#4A67FF" strokeWidth="2" fill="none" strokeDasharray="22 22" strokeLinecap="round" />
                                            </svg>
                                        )}
                                        <div>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#151313' }}>{r['label']}</div>
                                            {r['status'] === 'pending' && !isRunning && <div style={{ fontSize: 12, color: '#AAACB5', marginTop: 2 }}>Waiting...</div>}
                                            {isRunning && r['status'] === 'pending' && <div style={{ fontSize: 12, color: '#4A67FF', marginTop: 2 }}>Running...</div>}
                                            {r['status'] === 'error' && !r['data'] && <div style={{ fontSize: 12, color: '#F0606C', marginTop: 2 }}>{r['message'] || 'Migration failed'}</div>}
                                        </div>
                                    </div>

                                    {/* Stat tiles */}
                                    {r['data'] && (
                                        <>
                                            <div className="df fww" style={{ gap: 8, marginBottom: 12 }}>
                                                <StatTile label="Total Companies" value={r['data']['totalCompanies']} color="#002D63" />
                                                <StatTile label="Processed" value={r['data']['processed']} color="#4A67FF" />
                                                <StatTile label="Answers Migrated" value={r['data']['migrated']} color="#09B255" />
                                                <StatTile label="Skipped" value={r['data']['skipped']} color="#F0AE0D" />
                                                <StatTile label="Failed" value={r['data']['failed']} color="#F0606C" />
                                            </div>
                                            {r['data']['details'] && r['data']['details'].length > 0 && (
                                                <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #DDE2F2' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#F0F2FD' }}>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#002D63', borderBottom: '1px solid #DDE2F2' }}>Company</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#09B255', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Migrated</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#F0AE0D', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Skipped</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#AAACB5', borderBottom: '1px solid #DDE2F2', whiteSpace: 'nowrap' }}>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {r['data']['details'].map((d, di) => (
                                                                <tr key={di} style={{ background: di % 2 === 0 ? '#fff' : '#F7F8FE', borderBottom: di < r['data']['details'].length - 1 ? '1px solid #DDE2F2' : 'none' }}>
                                                                    <td style={{ padding: '8px 12px', color: '#151313', fontWeight: 500 }}>{d['cName'] || d['companyId']}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'center', color: d['migrated'] > 0 ? '#09B255' : '#AAACB5', fontWeight: 600 }}>{d['migrated']}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'center', color: d['skipped'] > 0 ? '#F0AE0D' : '#AAACB5', fontWeight: 600 }}>{d['skipped']}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                        {d['noAnswers']
                                                                            ? <span style={{ background: '#F0F2FD', color: '#5E5F66', borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>No Answers</span>
                                                                            : d['errors'] && d['errors'].length > 0
                                                                                ? <span style={{ background: '#FFF5F5', color: '#F0606C', borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>Error</span>
                                                                                : d['migrated'] > 0
                                                                                    ? <span style={{ background: '#F0FFF6', color: '#09B255', borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>Migrated</span>
                                                                                    : <span style={{ background: '#FFFBF0', color: '#F0AE0D', borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>Skipped</span>
                                                                        }
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
